import { useState, useCallback, useRef } from 'react';
import { searchPlaces, searchNearbyRestaurants, searchCuisine, calculateDistance } from '../utils/googlePlaces';
import { useQuery } from 'convex/react';
import { api } from '../convex/_generated/api';

/**
 * Custom hook for Google Places API Text Search functionality
 */
export const useGooglePlacesSearch = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  const searchTimeoutRef = useRef(null);
  const [disgoRestaurants, setDisgoRestaurants] = useState([]);

  /**
   * Perform a text search using Google Places API
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @param {Object} options.location - Current location for bias
   * @param {number} options.maxResults - Maximum number of results
   * @param {boolean} options.debounce - Whether to debounce the search (default: true)
   * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 500)
   */
  const search = useCallback(async (query, options = {}) => {
    const {
      location,
      maxResults = 20,
      debounce = true,
      debounceMs = 200
    } = options;

    // Clear previous timeout if debouncing
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const performSearch = async () => {
      if (!query || query.trim().length === 0) {
        setSearchResults([]);
        setDisgoRestaurants([]);
        setLastSearchQuery('');
        return;
      }

      setIsSearching(true);
      setSearchError(null);
      setLastSearchQuery(query);

      try {
        // Search Google Places first
        const googleResults = await searchPlaces(query, {
          locationBias: location ? {
            latitude: location.latitude,
            longitude: location.longitude,
            radius: 50000 // 50km radius
          } : undefined,
          maxResultCount: maxResults,
          rankPreference: 'RELEVANCE' // Use relevance ranking for better partial matches
        });

        // For now, we'll search Disgo restaurants in a separate call
        // This will be handled by the component using useQuery
        const disgoResults = [];

        // Process Disgo restaurants
        const processedDisgoRestaurants = disgoResults.map(restaurant => ({
          ...restaurant,
          id: restaurant._id,
          name: restaurant.name,
          // Address comes from Google Place Details at display time — do not use DB address.
          address: "",
          latitude: restaurant.latitude,
          longitude: restaurant.longitude,
          rating: restaurant.rating,
          userRatingCount: restaurant.userRatingCount || 0,
          priceLevel: restaurant.price ? (restaurant.price === '$' ? 1 : restaurant.price === '$$' ? 2 : restaurant.price === '$$$' ? 3 : 4) : null,
          types: restaurant.googleTypes || ['restaurant'],
          isDisgoRestaurant: true,
          isPartner: restaurant.isPartner || false,
          cuisine: restaurant.cuisine,
          distance: location ? calculateDistance(
            location.latitude,
            location.longitude,
            restaurant.latitude,
            restaurant.longitude
          ) : null
        }));

        // Process Google Places results
        let processedGoogleResults = googleResults;
        if (location && googleResults.length > 0) {
          processedGoogleResults = googleResults.map(place => ({
            ...place,
            distance: calculateDistance(
              location.latitude,
              location.longitude,
              place.latitude,
              place.longitude
            )
          }));

          // Sort by distance
          processedGoogleResults.sort((a, b) => a.distance - b.distance);
        }

        // Combine results with Disgo restaurants first
        const combinedResults = [...processedDisgoRestaurants, ...processedGoogleResults];
        
        // Sort by distance if location is provided
        if (location) {
          combinedResults.sort((a, b) => (a.distance || 0) - (b.distance || 0));
        }

        setDisgoRestaurants(processedDisgoRestaurants);
        setSearchResults(combinedResults);

      } catch (error) {
        console.error('Search error:', error);
        setSearchError(error.message);
        setSearchResults([]);
        setDisgoRestaurants([]);
      } finally {
        setIsSearching(false);
      }
    };

    if (debounce) {
      searchTimeoutRef.current = setTimeout(performSearch, debounceMs);
    } else {
      await performSearch();
    }
  }, []);

  /**
   * Search for nearby restaurants
   * @param {Object} location - Current location
   * @param {string} query - Optional search query
   * @param {Object} options - Additional options
   */
  const searchNearby = useCallback(async (location, query = '', options = {}) => {
    if (!location) {
      setSearchError('Location is required for nearby search');
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const results = await searchNearbyRestaurants(location, query, options);
      setSearchResults(results);
      setLastSearchQuery(query || 'restaurants near me');
    } catch (error) {
      console.error('Nearby search error:', error);
      setSearchError(error.message);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  /**
   * Search for specific cuisine
   * @param {string} cuisine - Cuisine type
   * @param {Object} location - Current location
   * @param {Object} options - Additional options
   */
  const searchCuisineType = useCallback(async (cuisine, location, options = {}) => {
    if (!cuisine) {
      setSearchError('Cuisine type is required');
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const results = await searchCuisine(cuisine, location, options);
      setSearchResults(results);
      setLastSearchQuery(`${cuisine} restaurants`);
    } catch (error) {
      console.error('Cuisine search error:', error);
      setSearchError(error.message);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  /**
   * Clear search results and reset state
   */
  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setSearchError(null);
    setLastSearchQuery('');
    setIsSearching(false);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, []);

  /**
   * Check if the current search results are from Google Places
   * @returns {boolean} True if results are from Google Places
   */
  const hasGooglePlacesResults = useCallback(() => {
    return searchResults.length > 0 && searchResults.some(place => place.isGooglePlace);
  }, [searchResults]);

  /**
   * Get unique place types from search results
   * @returns {Array} Array of unique place types
   */
  const getPlaceTypes = useCallback(() => {
    const types = new Set();
    searchResults.forEach(place => {
      if (place.types) {
        place.types.forEach(type => types.add(type));
      }
    });
    return Array.from(types);
  }, [searchResults]);

  return {
    searchResults,
    isSearching,
    searchError,
    lastSearchQuery,
    search,
    searchNearby,
    searchCuisineType,
    clearSearch,
    hasGooglePlacesResults,
    getPlaceTypes,
    disgoRestaurants
  };
};
